import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import { Alert } from '@backstage/ui';
import useAsync from 'react-use/esm/useAsync';
import { AccessReport, AzureAccessApi, azureAccessApiRef } from '../api';
import { AccessReportView } from './AccessReportView';

/**
 * Loads one access report and shows it, or says why it cannot be shown —
 * most often because only the admin groups may see anyone else's access.
 */
export const AccessReportLoader = ({
  load,
  deps,
}: {
  load: (api: AzureAccessApi) => Promise<AccessReport>;
  deps: unknown[];
}) => {
  const api = useApi(azureAccessApiRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { value, loading, error } = useAsync(() => load(api), [api, ...deps]);

  if (loading) {
    return <Progress />;
  }
  if (error) {
    const status = error instanceof ResponseError ? error.statusCode : 0;
    if (status === 403) {
      return (
        <Alert
          status="info"
          icon
          title="Only the admin groups can see this"
          description="Everyone can see their own Azure access, on the My Azure access page."
        />
      );
    }
    if (status === 404) {
      return (
        <Alert
          status="info"
          icon
          title="No Azure access to show"
          description={error.message}
        />
      );
    }
    return (
      <Alert
        status="danger"
        icon
        title="Could not load Azure access"
        description={error.message}
      />
    );
  }
  return value ? <AccessReportView report={value} /> : null;
};
